import { FC } from 'react';

import { OrderItemType, OrderItem as GeneratedOrderItem } from '@mews-commander/data-access';
import { OrderItem, WithScope, WithTimeUnit } from '@mews-commander/types';
import {
    ConvertedCurrencyValue,
    FlexItemNoShrink,
    FlexSpaceBetweenAlignCenter,
    LocalizedUtcTimeUnit,
    SpacedLongDash,
    SpacingSmallLeft,
} from '@mews-commander/ui';
import { Typography, Stack } from '@optimus-web/core';
import { TimeUnit, UnicodeEntity } from '@mews/enums';
import { LocalizedText } from '@mews/framework';
import { createHandler } from '@mews/std';
import { TextKey } from '@mews-commander/enums';
import { OrderItemName } from '../../../../components';
import RebatedTag from '../../../../rebate/components/RebatedTag';
import RebateTag from '../../../../rebate/components/RebateTag';
import {
    getOrderItemCancellationReason,
    getOrderItemRebateIndex,
    getOrderItemRebateReason,
    isOrderItemRebate,
} from '../../../../rebate/selectors/orderItems';
import { CancellationFeeReason } from '../OrderItemProductLabel/CancellationFeeReason';
import { PartialMonthDayRange } from '../../../../../reservationModule/components/ItemsTab/PartialMonthDayRange';
import OrderItemContextMenu from './OrderItemContextMenu';

interface OrderItemListItemTextProps extends WithTimeUnit, WithScope {
    orderItem: OrderItem;
    isRebated: boolean;
    showNotes?: boolean;
}

type HandlerValue = (item: OrderItem, timeUnit: TimeUnit) => React.ReactNode;

const AdditionalExpense = () => (
    <>
        {SpacedLongDash}
        <LocalizedText textKey={TextKey.AdditionalExpense} />
    </>
);

const SpaceOrderStartDate = ({ startUtc, proRataStartUtc, proRataEndUtc }: OrderItem, timeUnit: TimeUnit) =>
    startUtc ? (
        <>
            {SpacedLongDash}
            {proRataStartUtc && proRataEndUtc ? (
                <PartialMonthDayRange proRataStartUtc={proRataStartUtc} proRataEndUtc={proRataEndUtc} />
            ) : (
                <LocalizedUtcTimeUnit timeUnit={timeUnit} utcString={startUtc} />
            )}
        </>
    ) : null;

const OrderItemConsumedDate = ({ consumedUtc, proRataStartUtc, proRataEndUtc }: OrderItem, timeUnit: TimeUnit) =>
    consumedUtc ? (
        <>
            {SpacedLongDash}
            {proRataStartUtc && proRataEndUtc ? (
                <PartialMonthDayRange proRataStartUtc={proRataStartUtc} proRataEndUtc={proRataEndUtc} />
            ) : (
                <LocalizedUtcTimeUnit timeUnit={timeUnit} utcString={consumedUtc} />
            )}
        </>
    ) : null;

const getNotes = createHandler<string, HandlerValue | null>(
    {
        [OrderItemType.SpaceOrder]: SpaceOrderStartDate,
        [OrderItemType.ProductOrder]: OrderItemConsumedDate,
        [OrderItemType.ProductOrderRebate]: () => <AdditionalExpense />,
        [OrderItemType.AdditionalExpenseRebate]: () => <AdditionalExpense />,
        [OrderItemType.NightRebate]: () => <AdditionalExpense />,
    },
    null
);

const OrderItemListItemText: FC<OrderItemListItemTextProps> = ({ orderItem, showNotes, isRebated, timeUnit }) => {
    const { totalAmount, isReinstated } = orderItem as unknown as GeneratedOrderItem;
    const isRebate = isOrderItemRebate(orderItem);
    const rebateReason = getOrderItemRebateReason(orderItem);
    const rebateIndex = getOrderItemRebateIndex(orderItem);
    // @ts-expect-error OrderItem types are not matching
    const cancellationReason = getOrderItemCancellationReason(orderItem);

    return (
        <FlexSpaceBetweenAlignCenter>
            <Stack itemAlignment="center">
                {isRebate && !isReinstated ? (
                    <>
                        <RebateTag rebateReason={rebateReason} rebateIndex={rebateIndex} />
                        <SpacingSmallLeft />
                    </>
                ) : null}
                {isRebated ? (
                    <>
                        <RebatedTag />
                        <SpacingSmallLeft />
                    </>
                ) : null}

                <Typography textStyle="bodyMediumStrong" as="div">
                    <OrderItemName orderItem={orderItem} lastLayer />
                </Typography>
                <Typography textStyle="bodyMediumStrong" variant="secondary" as="div">
                    {getNotes(orderItem.type, orderItem, timeUnit)}
                </Typography>
                {orderItem.notes && showNotes ? (
                    <Typography textStyle="bodyMediumStrong" variant="secondary" as="div">
                        {SpacedLongDash}
                        {orderItem.notes}
                    </Typography>
                ) : null}
                {cancellationReason && (
                    <Typography textStyle="bodyMediumStrong" variant="secondary" as="div">
                        {SpacedLongDash}
                        <CancellationFeeReason cancellationReason={cancellationReason} />
                    </Typography>
                )}
                {timeUnit !== TimeUnit.Month && (
                    <Typography textStyle="bodyMediumStrong" variant="secondary" as="div">
                        {SpacedLongDash}
                        {orderItem.unitMultiplier}
                        {UnicodeEntity.MultiplicationSign}
                    </Typography>
                )}
            </Stack>
            <FlexItemNoShrink>
                <Stack
                    itemAlignment="center"
                    distribution="space-between"
                    spacing="300"
                    onClick={(e) => e.stopPropagation()}
                >
                    <ConvertedCurrencyValue amount={totalAmount} />
                    <OrderItemContextMenu orderItem={orderItem as unknown as GeneratedOrderItem} />
                </Stack>
            </FlexItemNoShrink>
        </FlexSpaceBetweenAlignCenter>
    );
};

export default OrderItemListItemText;
